import React, {useRef, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import EllipsesControl from '../EllipsesControl/EllipsesControl';
import Modal from '../Modal/Modal';
import RelateItemList from '../RelateList/RelateItemList';
import {labels, currentResource} from '../../../util/utils';
import {specialBodyExtendedPagingOptions, ScotType} from '../../../util/uiconstants';
import {useEngagementId} from '../../../util/customHooks';
import {getAllScots} from '../../../actions/scots/scotsactions';
import {isNullOrUndefined, isNullOrWhitespace} from '../../../util/utils';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {IconoirCustomEstimate} from '@ey/motif-react-wrapper/assets/icons/custom';
import {relateSCOTToRisk, unRelateSCOTToRisk} from '../../../actions/Risk/riskactions';

export default function RelateScotModal({riskId, riskName, isModalOpen, onClickRelateModal, handleCheckChange}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const riskScots = useSelector((state) => state.risk.scots);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(specialBodyExtendedPagingOptions.defaultPageSize);
	const riskScotsList = useRef(-1);

	useEffect(() => {
		riskScotsList.current = riskScots?.map((scotItem) => scotItem.scotID).join(',') || -1;
	}, [riskScots]);

	const getEstimatedScot = (scot) => {
		return scot.scotTypes?.find((scotTypeId) => scotTypeId === ScotType.Estimation) > 0 ? true : false;
	};

	const renderItemComponent = React.useCallback((scot) => {
		return (
			<Wrapper>
				<section className="scotname">
					<EllipsesControl noOfLines={1} content={scot.scotName} tooltip={scot.scotName} />
				</section>
				<section title={labels.relateSCOT.estimate} className="estimateicon">
					{getEstimatedScot(scot) ? (
						<MotifIcon iconFunction={IconoirCustomEstimate} title={labels.relateSCOT.estimate} size="20" />
					) : (
						<></>
					)}
				</section>
			</Wrapper>
		);
	}, []);

	const selected = React.useCallback(
		(entity) => {
			let selectedWCGW = false;
			if (riskScotsList.current !== -1 && riskScotsList.current.split(',').includes(entity.id.toString())) {
				selectedWCGW = true;
			}
			return selectedWCGW;
		},
		[riskScots]
	);

	const onCheckChange = (checked, scotItem) => {
		let promise = null;
		if (checked) {
			let data = scotItem.id;
			promise = dispatch(relateSCOTToRisk(engagementId, riskId, data)).then(() => {
				let scotData = {
					isEstimate: scotItem.scotTypes?.find((scotTypeId) => scotTypeId === 3) > 0 ? true : false,
					isNonRoutine: scotItem.scotTypes?.find((scotTypeId) => scotTypeId === 2) > 0 ? true : false,
					isRoutine: scotItem.scotTypes?.find((scotTypeId) => scotTypeId === 1) > 0 ? true : false,
					riskID: riskId,
					scotID: scotItem.id,
					scotName: scotItem.scotName,
					strategyTypeID: scotItem.strategyTypeId
				};
				handleCheckChange(checked, scotData);
			});
		} else {
			promise = dispatch(unRelateSCOTToRisk(engagementId, riskId, scotItem.id)).then(() => {
				let scotData = {
					riskID: riskId,
					scotID: scotItem.id
				};
				handleCheckChange(checked, scotData);
			});
		}
		return promise;
	};

	const data = React.useCallback((page, pageSize, searchText, sortColumn, selected) => {
		setPageSize(pageSize);
		setPage(page);
		let queryString = {
			page: page,
			pageSize: pageSize,
			includeControl: true,
			includePagination: true
		};
		if (selected) {
			queryString.filterSCOT = riskScotsList.current;
		}

		if (!isNullOrWhitespace(searchText)) {
			queryString.searchString =
				isNullOrWhitespace(searchText) && isNullOrUndefined(searchText) ? '' : searchText.trim();
		}

		return dispatch(getAllScots(engagementId, {resolve: true}, queryString));
	}, []);

	const mapResponse = (response) => {
		if (response) {
			let scotList = response.data && response.data.length > 0 ? response.data : [];
			return {
				page: response.page,
				pages: response.pages,
				totalEntityCount: response.totalEntityCount,
				data: scotList
			};
		}
	};

	let configuration = React.useMemo(
		() => ({
			noResultsLabel: labels.relateSCOT.noResultsFound,
			data: (page, pageSize, searchString, sortColumn, selected) =>
				data(page, pageSize, searchString, sortColumn, selected),
			mapResponse: (response) => mapResponse(response),
			selected: (item) => selected(item),
			onCheckBoxChange: (item, checked) => onCheckChange(checked, item),
			render: renderItemComponent,
			noDataLabel: labels.relateSCOT.noScotCreated
		}),
		[]
	);

	const componentHeader = React.useCallback(() => {
		return (
			<section className="filter-scot-container">
				<section className="related-scot">
					<span className="title">{labels.relateSCOT.estimate}: </span>
					<EllipsesControl noOfLines={1} content={riskName} tooltip={riskName} />
				</section>
			</section>
		);
	}, []);

	const headerComponent = (totalEntityCount) => {
		return (
			<section className="scot-header">
				<section className="scot-name">{labels.relateSCOT.headerName}</section>
				<section className="scot-count">
					{totalEntityCount > 0 && (
						<section>
							{`${totalEntityCount > 1 ? labels.relateSCOT.scotsCounter : labels.relateSCOT.scotCounter}`.replace(
								'{0}',
								totalEntityCount
							)}
						</section>
					)}
				</section>
			</section>
		);
	};

	return (
		<>
			{
				<Modal
					modalsize="medium"
					show={isModalOpen}
					title={labels.relateSCOT.header}
					onHide={onClickRelateModal}
					closeTitle={labels.cancelLabel}
					showFooter={false}
				>
					<StyledSection>
						<RelateItemList
							key={riskId}
							listHeaders={headerComponent}
							componentHeader={componentHeader}
							search
							pagination
							page={page}
							pageSize={pageSize}
							optionsPerPage={specialBodyExtendedPagingOptions.options}
							configuration={configuration}
							showAddedLabel
							addedLabel={labels.added}
							searchPlaceholder={labels.searchPlaceholder}
							paginationIconHover={currentResource.pagingSvgHoverText}
							paginationShowText={labels.pagingShowtext}
							showSelectedOnlyCheckBox
							showSelectedOnlyText={labels.relateSCOT.showOnlyRelated}
							onPageChanged={(page) => {
								data(page);
							}}
						/>
					</StyledSection>
				</Modal>
			}
		</>
	);
}

const Wrapper = styled.section`
	width: 100%;
	display: flex;

	.scotname {
		width: 99%;
		font-size: var(--px-14);
		font-weight: 300;
		padding-right: var(--px-14);
		line-height: var(--px-27);
		vertical-align: middle;
		display: grid;
	}

	.estimateicon {
		.motif-icon {
			svg {
				path {
					fill: var(--neutrals-900);
				}
			}
		}
	}
	.estimate {
		margin-left: auto;
	}
`;

const StyledSection = styled.section`
	height: 100%;
	.relate-item-list-labelTxt {
		width: 85%;
		max-width: 85%;
		line-height: normal;
	}
	.RelateItemsListHeaderSelectedOnly {
		max-width: 100%;
		display: flex;
		align-items: center;
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			margin: var(--px-10) 0;
			max-width: 100%;
		}
		.RelateItemsListHeaderSelectedOnlyCheckbox {
			.motif-checkbox-label {
				width: calc(100% - var(--px-20));
			}
		}
	}
	.RelateItemsListHeaderComponent {
		width: 100% !important;
		max-width: 70%;
	}
	.filter-scot-container {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: var(--px-10) 0;
		.related-scot {
			display: flex;
			width: 100%;
			font-size: var(--px-14);
			font-weight: 300;
			color: var(--neutrals-900);

			.title {
				margin-right: var(--px-4);
				font-size: var(--px-14);
				font-weight: 300;
			}

			.ellipses {
				font-size: var(--px-14);
				font-weight: 400;
				width: 80%;
			}
		}
	}
	.scot-name {
		font-size: var(--px-14);
		font-weight: 400;
		line-height: var(--px-20);
		color: var(--neutrals-900);
		margin-left: var(--px-48);
	}
	.scot-count {
		display: flex;
		align-items: center;
		margin-right: var(--px-16);
		margin-left: auto;
		text-align: right;
		font-size: var(--px-12);
		font-weight: 400;
		line-height: var(--px-18);
		color: var(--neutrals-900);
		width: auto;
	}
	.scot-header {
		display: flex;
	}
`;
